import { useEffect, useState, useCallback } from 'react';
import { createColumnHelper } from '@tanstack/react-table';
import { useWebAuth } from '../../../hooks/useWebAuth';
import {
  getAdminSubscriptions,
  getSubscriptionMetrics,
  grantPro,
  revokePro,
} from '../../../api/admin-api';
import { AdminTable } from '../components/AdminTable';
import { StatCard } from '../components/StatCard';
import './SubscriptionsPage.scss';

interface Subscription {
  userId: string;
  email?: string;
  plan: string;
  status?: string;
  currentPeriodEnd?: string;
  paddleSubscriptionId?: string;
  createdAt: string;
}

interface Metrics {
  paidUsers: number;
  mrr: number;
  conversionRate: number;
  churnRate: number;
  failedPayments: number;
}

const columnHelper = createColumnHelper<Subscription>();

export function SubscriptionsPage() {
  const { token } = useWebAuth();
  const [data, setData] = useState<Subscription[]>([]);
  const [metrics, setMetrics] = useState<Metrics | null>(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(
    (p: number, f: Record<string, string> = filters) => {
      if (!token) return;
      setLoading(true);
      Promise.all([
        getAdminSubscriptions(token, { page: String(p), limit: '20', ...f }),
        getSubscriptionMetrics(token),
      ])
        .then(([subs, m]) => {
          setData(subs.data);
          setTotalPages(subs.totalPages);
          setPage(subs.page);
          setMetrics(m);
        })
        .finally(() => setLoading(false));
    },
    [token, filters],
  );

  useEffect(() => {
    fetchData(1);
  }, [fetchData]);

  const handleGrantPro = async (userId: string) => {
    if (!token) return;
    await grantPro(token, userId);
    fetchData(page);
  };

  const handleRevokePro = async (userId: string) => {
    if (!token) return;
    await revokePro(token, userId);
    fetchData(page);
  };

  const columns = [
    columnHelper.accessor('email', {
      header: 'User',
      cell: (info) => info.getValue() ?? 'Anonymous',
    }),
    columnHelper.accessor('plan', { header: 'Plan' }),
    columnHelper.accessor('status', {
      header: 'Status',
      cell: (info) => info.getValue() ?? '-',
    }),
    columnHelper.accessor('currentPeriodEnd', {
      header: 'Renewal Date',
      cell: (info) => {
        const val = info.getValue();
        return val ? new Date(val).toLocaleDateString() : '-';
      },
    }),
    columnHelper.accessor('paddleSubscriptionId', {
      header: 'Paddle ID',
      cell: (info) => info.getValue() ?? '-',
    }),
    columnHelper.accessor('createdAt', {
      header: 'Created',
      cell: (info) => new Date(info.getValue()).toLocaleDateString(),
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      cell: (info) => {
        const sub = info.row.original;
        return (
          <div className="subscriptions-page__actions">
            {sub.plan === 'free' ? (
              <button onClick={() => handleGrantPro(sub.userId)}>Grant Pro</button>
            ) : (
              <button onClick={() => handleRevokePro(sub.userId)}>Revoke Pro</button>
            )}
          </div>
        );
      },
    }),
  ];

  const handleFilterChange = (key: string, value: string) => {
    const newFilters = { ...filters };
    if (value) {
      newFilters[key] = value;
    } else {
      delete newFilters[key];
    }
    setFilters(newFilters);
    fetchData(1, newFilters);
  };

  return (
    <div className="subscriptions-page">
      <h1 className="subscriptions-page__title">Subscriptions</h1>

      {metrics && (
        <div className="subscriptions-page__metrics">
          <StatCard label="Paid Users" value={metrics.paidUsers} />
          <StatCard label="MRR" value={`$${metrics.mrr}`} />
          <StatCard label="Conversion Rate" value={`${(metrics.conversionRate * 100).toFixed(1)}%`} />
          <StatCard label="Churn Rate" value={`${(metrics.churnRate * 100).toFixed(1)}%`} />
          <StatCard label="Failed Payments" value={metrics.failedPayments} />
        </div>
      )}

      <div className="subscriptions-page__filters">
        <select onChange={(e) => handleFilterChange('status', e.target.value)} defaultValue="">
          <option value="">All Statuses</option>
          <option value="active">Active</option>
          <option value="cancelled">Cancelled</option>
          <option value="past_due">Past Due</option>
          <option value="paused">Paused</option>
        </select>
        <select onChange={(e) => handleFilterChange('plan', e.target.value)} defaultValue="">
          <option value="">All Plans</option>
          <option value="free">Free</option>
          <option value="pro">Pro</option>
        </select>
      </div>

      {loading ? (
        <div>Loading...</div>
      ) : (
        <AdminTable
          data={data}
          columns={columns}
          page={page}
          totalPages={totalPages}
          onPageChange={(p) => fetchData(p)}
          onSortChange={(sortBy, order) => fetchData(1, { ...filters, sortBy, order })}
        />
      )}
    </div>
  );
}
